// taken from @sprint/sprint-react-components/profile_lists/ProfileAddEditModal.scss
import {
    AsyncDropDownPaginated,
    Checkbox,
    DropDown,
    Modal,
    ModalType,
    OptionTypeBase,
    PeopleTableType,
    User,
    UserState,
    isProfileOwnedByMe,
} from '@sprint/sprint-react-components';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
// import { EntityTarget } from '../models/Enums';
import EducationDataList from '@client/Applications/EducationDataLists/Models/EducationDataList';
import { EntityTarget } from '@sprint/sprint-react-components';
import React from 'react';
import './ViewAddEditModal.scss';
export enum ViewAddEditModalType {
    LIST = 'List',
    SAVED_SEARCH = 'Saved Search',
}

interface Props {
    modalType: ViewAddEditModalType;
    profile: EducationDataList;
    modalShown: boolean;
    closeModal: () => void;
    onSubmit: (profile: EducationDataList) => Promise<boolean>;
    checkProfileNameIsUnique?: (
        profileName: string,
        profileId?: number,
        profileTarget?: EntityTarget,
    ) => Promise<boolean>;
    peopleTableType?: PeopleTableType;
    user: User | UserState;
    favouriteText?: string | undefined;
    entityTypeSingular?: string;
    getAvailableFolders?: (filter: string, loadedOptions: any, page: number) => Promise<any>;
    withSchedule?: boolean;
}

const ListAddEditModal: FunctionComponent<Props> = (props: Props) => {
    const focusRef = useRef<HTMLInputElement>(null);

    const [profileName, setProfileName] = useState(props.profile.name ?? '');
    const [profileDescription, setProfileDescription] = useState(props.profile.description ?? '');
    const [profileShared, setProfileShared] = useState(props.profile.shared ?? false);
    const [profilePinned, setProfilePinned] = useState(props.profile.pinned ?? false);
    const [profileFavourited, setProfileFavourited] = useState(props.profile.favourited ?? false);

    const [clientGroupValue, setClientGroupValue] = useState<OptionTypeBase | undefined>({
        value: props.profile.clientGroupInfo?.id,
        label: props.profile.clientGroupInfo?.name,
    });

    const [allowInMainTable, setAllowInMainTable] = useState(props.profile.allowInMainTable);
    const [allowInEmbedTable, setAllowInEmbedTable] = useState(props.profile.allowInEmbedTable);

    const validationStateDefault = {
        name: true,
        allowInTable: true,
    };
    const [validationState, setValidationState] = useState(validationStateDefault);
    const [nameValidationFeeback, setNameValidationFeedback] = useState<Record<string, string>>({ name: '' });

    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const getOrdinal = (n: number) => {
        if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
        switch (n % 10) {
            case 1:
                return `${n}st`;
            case 2:
                return `${n}nd`;
            case 3:
                return `${n}rd`;
            default:
                return `${n}th`;
        }
    };
    const dates = Array.from({ length: 28 }, (_, i) => i + 1);
    const [scheduleType, setScheduleType] = useState<'none' | 'daily' | 'weekly' | 'monthly'>(
        props.profile.dataSync ?? 'none',
    );
    const [weeklyDay, setWeeklyDay] = useState('Monday');
    const [monthlyDay, setMonthlyDay] = useState(1);

    const [isConfigured, setIsConfigured] = useState(false);

    const validate = async (): Promise<boolean> => {
        let nameValid: boolean = false;
        if (profileName !== '') {
            if (props.checkProfileNameIsUnique) {
                nameValid = await props.checkProfileNameIsUnique(profileName, props.profile.id, props.profile.target);
                if (!nameValid) {
                    setNameValidationFeedback({
                        name: `Sorry this ${props.entityTypeSingular} name is in use, please choose another name.`,
                    });
                }
            } else {
                nameValid = true;
            }
        } else {
            setNameValidationFeedback({ name: 'This field is required.' });
        }

        // if the people table type is supplied make sure one is selected
        const inTableValid: boolean = props.peopleTableType && !allowInMainTable && !allowInEmbedTable ? false : true;

        setValidationState({ name: nameValid, allowInTable: inTableValid });
        return nameValid && inTableValid;
    };

    const onSubmit = async (): Promise<boolean> => {
        if (!(await validate())) {
            return false;
        }

        const profile: EducationDataList = {
            id: props.profile.id,
            target: props.profile.target,
            name: profileName,
            description: profileDescription,
            shared: profileShared,
            allowInMainTable,
            allowInEmbedTable,
        };

        if (clientGroupValue?.value) {
            profile.clientGroupInfo = { id: clientGroupValue.value, name: clientGroupValue.label };
        }

        if (props.withSchedule) {
            profile.dataSync = scheduleType;
            switch (scheduleType) {
                case 'none':
                case 'daily':
                    profile.dataSyncOption = '';
                    break;
                case 'weekly':
                    profile.dataSyncOption = weeklyDay;
                    break;
                case 'monthly':
                    profile.dataSyncOption = monthlyDay.toString();
                    break;
            }
        }

        profile.pinned = profilePinned;
        profile.favourited = profileFavourited;

        return props.onSubmit(profile).then(() => {
            props.closeModal();
            return true;
        });
    };

    const reset = () => {
        setProfileName('');
        setProfileDescription('');
        setProfileShared(false);
        setProfilePinned(false);
        setProfileFavourited(false);
        setClientGroupValue(undefined);
        setScheduleType('none');
        setWeeklyDay('Monday');
        setMonthlyDay(1);

        setNameValidationFeedback({ name: '' });
        setValidationState(validationStateDefault);
    };

    // If profile is changed in props, make sure states update
    useEffect(() => {
        setProfileName(props.profile.name ?? '');
        setProfileDescription(props.profile.description ?? '');
        setProfileShared(props.profile.shared ?? false);
        setProfilePinned(props.profile.pinned ?? false);
        setProfileFavourited(props.profile.favourited ?? false);
        setClientGroupValue({
            value: props.profile.clientGroupInfo?.id,
            label: props.profile.clientGroupInfo?.name,
        });
        setAllowInMainTable(props.profile.allowInMainTable);
        setAllowInEmbedTable(props.profile.allowInEmbedTable);

        setScheduleType(props.profile.dataSync ?? 'none');
        setWeeklyDay(
            props.profile.dataSync === 'weekly' && props.profile.dataSyncOption
                ? props.profile.dataSyncOption
                : 'Monday',
        );
        setMonthlyDay(
            props.profile.dataSync === 'monthly' && props.profile.dataSyncOption
                ? parseInt(props.profile.dataSyncOption)
                : 1,
        );
    }, [props.profile]);

    useEffect(() => {
        if (props.modalShown) {
            if (document.getElementById('data-sync-configured')) {
                const value = (document.getElementById('data-sync-configured') as HTMLInputElement).value;

                setIsConfigured(!!value);
            }
            if (!props.profile.id) {
                reset();
            } else {
                setNameValidationFeedback({ name: '' });
                setValidationState(validationStateDefault);
            }
            focusRef.current?.focus();
        }
    }, [props.modalShown]);

    const listOwner = !props.profile.id ? true : isProfileOwnedByMe(props.profile, props.user);
    const modalTitle = props.entityTypeSingular
        ? props.entityTypeSingular
        : props.modalType == ViewAddEditModalType.SAVED_SEARCH
          ? ViewAddEditModalType.LIST
          : props.modalType;

    const dropdownMapLambda = (value: string | number, label: string | null = null) => {
        return {
            value: value,
            label: label ?? value,
        };
    };

    return (
        <Modal
            className="view-add-edit-modal"
            type={props.profile.id ? ModalType.EDIT : ModalType.ADD}
            close={() => props.closeModal()}
            onSubmit={onSubmit}
            isOpen={props.modalShown}
            title={props.profile.id ? `Edit ${modalTitle}` : `Save ${modalTitle}`}
            closeOnSubmitFailure={false}
        >
            <Form onSubmit={onSubmit}>
                <Form.Group>
                    <Form.Label>
                        Name <span className="required-field-marker">*</span>
                    </Form.Label>
                    <Form.Control
                        autoComplete="off"
                        ref={focusRef}
                        type="text"
                        isInvalid={!validationState.name}
                        value={profileName}
                        onChange={(event) => {
                            setProfileName(event.target.value);
                        }}
                        disabled={!listOwner}
                        data-cy={'saved-view-add-modal-name'}
                    />
                    <Form.Control.Feedback type="invalid">
                        {!validationState.name && nameValidationFeeback.name}
                    </Form.Control.Feedback>
                </Form.Group>
                {props.modalType != ViewAddEditModalType.SAVED_SEARCH && (
                    <Form.Group>
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            value={profileDescription}
                            onChange={(event) => setProfileDescription(event.target.value)}
                            disabled={!listOwner}
                        />
                    </Form.Group>
                )}
                {props.peopleTableType !== undefined && (
                    <Form.Group>
                        <Form.Label>Where this View can be used</Form.Label>
                        <Checkbox
                            className="form-checkbox"
                            label="Main People Table"
                            isChecked={!!allowInMainTable}
                            disabled={!listOwner}
                            onChange={(e) => setAllowInMainTable(e.target.checked)}
                        />
                        <Checkbox
                            className="form-checkbox"
                            label="Establishment's People Table"
                            isChecked={!!allowInEmbedTable}
                            disabled={!listOwner}
                            onChange={(e) => setAllowInEmbedTable(e.target.checked)}
                        />
                        {!validationState.allowInTable && (
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                You must select at least 1 option
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                )}
                {props.getAvailableFolders && (
                    <Form.Group>
                        <Form.Label>Folder</Form.Label>
                        <AsyncDropDownPaginated
                            id={'client_group_id'}
                            value={clientGroupValue}
                            onChange={(selected: OptionTypeBase) => {
                                setClientGroupValue(selected);
                            }}
                            isInvalid={false}
                            loadOptions={props.getAvailableFolders}
                            menuPortalTarget={document.body}
                            isClearable={true}
                        />
                    </Form.Group>
                )}
                <br />
                <Form.Group data-cy="saved-view-add-modal-checkboxes" className="checkbox-row">
                    <Checkbox
                        label="Share"
                        className="form-checkbox align-left"
                        isChecked={profileShared}
                        disabled={!listOwner}
                        onChange={(e) => setProfileShared(e.target.checked)}
                    />
                    <Checkbox
                        label="Pin"
                        className="form-checkbox align-center"
                        isChecked={profilePinned}
                        disabled={false}
                        onChange={(e) => setProfilePinned(e.target.checked)}
                    />
                    {props.modalType !== ViewAddEditModalType.SAVED_SEARCH && (
                        <Checkbox
                            label={props.favouriteText ? props.favouriteText : 'Favourite'}
                            className="form-checkbox align-right"
                            isChecked={profileFavourited}
                            disabled={false}
                            onChange={(e) => setProfileFavourited(e.target.checked)}
                        />
                    )}
                </Form.Group>
                {props.withSchedule && (
                    <section style={{ position: 'relative' }}>
                        {!isConfigured && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    background: 'rgba(255,255,255,0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 10,
                                }}
                            >
                                Data Sync has not been configured (<a href="/settings/data_sync">Configure</a>)
                            </span>
                        )}
                        <Form.Group>
                            <Form.Label>Data Sync Schedule</Form.Label>
                            <span className="radio-row">
                                <Form.Check
                                    type="radio"
                                    id="radio-none"
                                    name="radio-none"
                                    label="None"
                                    className="radio-button"
                                    custom
                                    checked={scheduleType === 'none'}
                                    onChange={() => setScheduleType('none')}
                                />
                            </span>
                            <span className="radio-row">
                                <Form.Check
                                    type="radio"
                                    id="radio-daily"
                                    name="radio-daily"
                                    label="Daily"
                                    className="radio-button"
                                    custom
                                    checked={scheduleType === 'daily'}
                                    onChange={() => setScheduleType('daily')}
                                />
                            </span>
                            <span className="radio-row">
                                <Form.Check
                                    type="radio"
                                    id="radio-weekly"
                                    name="radio-weekly"
                                    label="Weekly"
                                    className="radio-button"
                                    custom
                                    checked={scheduleType === 'weekly'}
                                    onChange={() => setScheduleType('weekly')}
                                />
                                <DropDown
                                    disabled={scheduleType !== 'weekly'}
                                    value={
                                        weeklyDay != null
                                            ? dropdownMapLambda(daysOfWeek.find((value: string) => value == weeklyDay)!)
                                            : null
                                    }
                                    isInvalid={false}
                                    onChange={(option: OptionTypeBase) => {
                                        setWeeklyDay(option.value);
                                    }}
                                    options={daysOfWeek.map((day) => dropdownMapLambda(day))}
                                    isClearable={false}
                                    menuPortalTarget={document.body}
                                />
                            </span>
                            <span className="radio-row">
                                <Form.Check
                                    type="radio"
                                    id="radio-monthly"
                                    name="radio-monthly"
                                    label="Monthly"
                                    className="radio-button"
                                    custom
                                    checked={scheduleType === 'monthly'}
                                    onChange={() => setScheduleType('monthly')}
                                />
                                <DropDown
                                    disabled={scheduleType !== 'monthly'}
                                    value={
                                        monthlyDay != null && !isNaN(monthlyDay)
                                            ? dropdownMapLambda(monthlyDay, getOrdinal(monthlyDay))
                                            : null
                                    }
                                    isInvalid={false}
                                    onChange={(option: OptionTypeBase) => {
                                        setMonthlyDay(option.value);
                                    }}
                                    options={dates.map((day) => dropdownMapLambda(day, getOrdinal(day)))}
                                    isClearable={false}
                                    menuPortalTarget={document.body}
                                />
                            </span>
                        </Form.Group>
                    </section>
                )}
            </Form>
        </Modal>
    );
};

export default ListAddEditModal;
